// src/pages/guides/ExposedAdminPanelsGuide.js
// Exposed Admin Panels Response Guide - SEO Playbook Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function ExposedAdminPanelsGuide() {
  const startTrial = useTrialCta('guide_trial:exposed-admin-panels');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Exposed Admin Panels Guide",
    "description": "How to discover, validate, and secure exposed admin panels, CI/CD dashboards, monitoring interfaces, and database UIs.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/exposed-admin-panels",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/exposed-admin-panels"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Exposed Admin Panels" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Exposed Admin Panels Guide | DriftAlarm"
        description="How to discover, validate, and secure exposed admin panels, CI/CD dashboards, monitoring interfaces, and database UIs. Mitigation patterns and prevention controls."
        path="/guides/exposed-admin-panels"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/exposed-admin-panels')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Exposed Admin Panels: Your Hidden Attack Surface</h1>
          <GuideByline path="/guides/exposed-admin-panels" />
          <GuideAnswer path="/guides/exposed-admin-panels" />
          <p className="guide-hero-intro">
            Management interfaces exposed to the internet are among the most common and dangerous
            findings in security assessments. From Jenkins to phpMyAdmin, attackers actively scan
            for these targets because admin access often means complete system compromise.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Common admin panel types and why they're targeted</li>
              <li>How to validate and assess exposure severity</li>
              <li>Immediate mitigation patterns</li>
              <li>Authentication hardening requirements</li>
              <li>Network restriction options</li>
              <li>Prevention through asset tagging and deployment guardrails</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#why-critical">Why This Is Critical</a></li>
            <li><a href="#common-panels">Common Admin Panel Types</a></li>
            <li><a href="#validation">Validation</a></li>
            <li><a href="#severity-assessment">Severity Assessment</a></li>
            <li><a href="#immediate-mitigation">Immediate Mitigation</a></li>
            <li><a href="#auth-hardening">Authentication Hardening</a></li>
            <li><a href="#network-restrictions">Network Restrictions</a></li>
            <li><a href="#logging-monitoring">Logging & Monitoring</a></li>
            <li><a href="#prevention">Prevention Controls</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="why-critical" className="guide-section">
          <h2>Why Exposed Admin Panels Are Critical</h2>

          <div className="callout callout-critical">
            <div className="callout-title">Instant P1 Finding</div>
            <div className="callout-content">
              <p>
                <strong>Any admin panel exposed to the internet without MFA and IP restrictions
                is an automatic P1.</strong> Admin interfaces provide direct access to system
                configuration, user data, and often the underlying infrastructure.
              </p>
            </div>
          </div>

          <p>
            Admin panels are high-value targets for several reasons:
          </p>
          <ul>
            <li><strong>Automated scanning:</strong> Attackers run tools that scan for common admin
                paths like /admin, /wp-admin, /jenkins, /phpmyadmin across the entire internet</li>
            <li><strong>Default credentials:</strong> Many admin interfaces ship with default
                usernames and passwords that are tried automatically</li>
            <li><strong>Full control:</strong> Successful access to an admin panel often means
                complete control over the application, its data, and sometimes the server</li>
            <li><strong>Lateral movement:</strong> Admin panels often contain credentials or
                configuration for other systems, enabling attackers to pivot</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">The Discovery Timeline</div>
            <div className="callout-content">
              <p>
                Exposed admin panels are discovered faster than you might expect. Automated
                scanners continuously enumerate common paths across all public IP addresses.
              </p>
              <p>
                <strong>Within hours of deployment, your admin panel will be found and probed.</strong>
                Don't assume obscurity protects you—it doesn't.
              </p>
            </div>
          </div>
        </section>

        <section id="common-panels" className="guide-section">
          <h2>Common Admin Panel Types</h2>
          <p>
            Admin panels come in many forms. Understanding the types helps you inventory and
            protect them:
          </p>

          <h3>Application Admin Dashboards</h3>
          <p>Content management and application backends:</p>
          <ul>
            <li><strong>WordPress:</strong> /wp-admin, /wp-login.php</li>
            <li><strong>Drupal:</strong> /admin, /user/login</li>
            <li><strong>Joomla:</strong> /administrator</li>
            <li><strong>Custom CMS:</strong> /admin, /backend, /manage</li>
            <li><strong>E-commerce:</strong> Shopify admin, Magento /admin, WooCommerce</li>
          </ul>

          <h3>CI/CD Panels</h3>
          <p>Build and deployment systems with access to code and infrastructure:</p>
          <ul>
            <li><strong>Jenkins:</strong> Usually on port 8080, often with /jenkins path</li>
            <li><strong>GitLab:</strong> Self-hosted instances with full repository access</li>
            <li><strong>TeamCity:</strong> JetBrains CI server</li>
            <li><strong>Bamboo:</strong> Atlassian CI/CD</li>
            <li><strong>ArgoCD:</strong> Kubernetes continuous delivery</li>
            <li><strong>GitHub Actions runners:</strong> Self-hosted runner management</li>
          </ul>

          <div className="callout callout-critical">
            <div className="callout-title">CI/CD = Keys to the Kingdom</div>
            <div className="callout-content">
              <p>
                Exposed CI/CD panels are particularly dangerous. They often contain:
              </p>
              <ul>
                <li>Source code repository access</li>
                <li>Deployment credentials for production systems</li>
                <li>API keys and secrets in build configurations</li>
                <li>The ability to deploy malicious code to production</li>
              </ul>
            </div>
          </div>

          <h3>Monitoring Dashboards</h3>
          <p>Observability tools that reveal system architecture and data:</p>
          <ul>
            <li><strong>Grafana:</strong> Metrics visualization, often with database access</li>
            <li><strong>Kibana:</strong> Elasticsearch frontend with log data</li>
            <li><strong>Prometheus:</strong> Metrics server with system information</li>
            <li><strong>Nagios / Icinga:</strong> Infrastructure monitoring</li>
            <li><strong>Zabbix:</strong> Enterprise monitoring with agent access</li>
            <li><strong>Datadog / New Relic:</strong> APM dashboards</li>
          </ul>

          <h3>Database UIs</h3>
          <p>Direct database access interfaces:</p>
          <ul>
            <li><strong>phpMyAdmin:</strong> MySQL/MariaDB web interface</li>
            <li><strong>pgAdmin:</strong> PostgreSQL administration</li>
            <li><strong>Adminer:</strong> Multi-database manager</li>
            <li><strong>Redis Commander:</strong> Redis web interface</li>
            <li><strong>MongoDB Compass Web:</strong> MongoDB management</li>
            <li><strong>Elasticsearch Head:</strong> Elasticsearch browser</li>
          </ul>

          <h3>Infrastructure Management</h3>
          <p>Server and hosting control panels:</p>
          <ul>
            <li><strong>cPanel / WHM:</strong> Web hosting control panel</li>
            <li><strong>Plesk:</strong> Server management</li>
            <li><strong>Webmin:</strong> Unix system administration</li>
            <li><strong>Cockpit:</strong> Linux server management</li>
            <li><strong>Router/Firewall UIs:</strong> Network device management</li>
            <li><strong>iLO / iDRAC / IPMI:</strong> Out-of-band server management</li>
          </ul>
        </section>

        <section id="validation" className="guide-section">
          <h2>Validation</h2>
          <p>
            When you discover a potentially exposed admin panel, validate the finding before
            escalating:
          </p>

          <h3>Step 1: Confirm Internet Reachability</h3>
          <ul>
            <li>Verify the panel is accessible from outside your network</li>
            <li>Test from a non-corporate IP (mobile hotspot, VPN to external location)</li>
            <li>Confirm it's not behind a VPN or IP whitelist you're unaware of</li>
          </ul>

          <h3>Step 2: Identify the Application</h3>
          <ul>
            <li>Note the application name and version if visible</li>
            <li>Check for known CVEs affecting this version</li>
            <li>Look for version-specific default credentials</li>
          </ul>

          <h3>Step 3: Assess Authentication Status</h3>
          <ul>
            <li>Does the panel require authentication?</li>
            <li>Is there a login page, or is content directly accessible?</li>
            <li>Are any features available without authentication?</li>
          </ul>

          <h3>Step 4: Document Evidence</h3>
          <ul>
            <li>Screenshot the login page or exposed interface</li>
            <li>Record the URL and any version information</li>
            <li>Note the timestamp of discovery</li>
            <li>Document what data or functionality is exposed</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Authorization Required</div>
            <div className="callout-content">
              <p>
                <strong>Do NOT attempt to log in or test credentials without explicit authorization.</strong>
                Simply documenting that the panel is exposed is sufficient to escalate.
                Unauthorized login attempts may violate computer access laws.
              </p>
            </div>
          </div>
        </section>

        <section id="severity-assessment" className="guide-section">
          <h2>Severity Assessment</h2>
          <p>
            Not all exposed admin panels carry the same risk. Assess severity based on
            authentication status and potential impact:
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Criteria</th>
                  <th>Response Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>P0 (Critical)</strong></td>
                  <td>No authentication, default creds work, or sensitive data visible</td>
                  <td>Immediate</td>
                </tr>
                <tr>
                  <td><strong>P1 (High)</strong></td>
                  <td>Authentication exists but no MFA, publicly accessible</td>
                  <td>24-72 hours</td>
                </tr>
                <tr>
                  <td><strong>P2 (Medium)</strong></td>
                  <td>Auth + MFA present but broad network access</td>
                  <td>This sprint</td>
                </tr>
                <tr>
                  <td><strong>P3 (Low)</strong></td>
                  <td>Internal only, or MFA + IP restricted</td>
                  <td>Scheduled</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Factors That Increase Severity</h3>
          <ul>
            <li><strong>Production vs Dev/Staging:</strong> Production admin panels are higher priority</li>
            <li><strong>Data sensitivity:</strong> Access to PII, financial data, or credentials elevates risk</li>
            <li><strong>Infrastructure control:</strong> Panels that control servers, containers, or
                cloud resources are critical</li>
            <li><strong>Cloud provider access:</strong> AWS Console, Azure Portal, GCP Console exposure
                is maximum severity</li>
            <li><strong>CI/CD access:</strong> Ability to modify or deploy code is critical</li>
          </ul>

          <div className="example-box">
            <div className="example-title">Severity Examples</div>
            <div className="example-content">
              <p><strong>P0:</strong> phpMyAdmin accessible without authentication, showing database tables</p>
              <p><strong>P1:</strong> Jenkins dashboard with login page, no MFA, publicly accessible</p>
              <p><strong>P2:</strong> Grafana with SSO + MFA, but accessible from any IP</p>
              <p><strong>P3:</strong> WordPress admin behind VPN with MFA enabled</p>
            </div>
          </div>
        </section>

        <section id="immediate-mitigation" className="guide-section">
          <h2>Immediate Mitigation</h2>
          <p>
            When you discover an exposed admin panel, take these steps immediately:
          </p>

          <h3>Step 1: Block External Access (First 30 Minutes)</h3>
          <div className="callout callout-info">
            <div className="callout-title">Priority Action</div>
            <div className="callout-content">
              <p>Block access from the internet at the network level:</p>
              <ul>
                <li>Cloud security group / NSG (for cloud workloads)</li>
                <li>Perimeter firewall (for on-premises)</li>
                <li>Reverse proxy / load balancer ACL</li>
                <li>Web application firewall (WAF) rules</li>
              </ul>
            </div>
          </div>

          <h3>Step 2: Verify the Block Is Effective</h3>
          <ul>
            <li>Test access from an external IP to confirm the panel is no longer reachable</li>
            <li>Verify internal access still works for legitimate administrators</li>
            <li>Document the change for change management</li>
          </ul>

          <h3>Step 3: Notify the Asset Owner</h3>
          <ul>
            <li>Identify who owns/manages this system</li>
            <li>Inform them of the exposure and the mitigation applied</li>
            <li>Coordinate on permanent remediation</li>
          </ul>

          <h3>Step 4: Review Access Logs</h3>
          <ul>
            <li>Check for unauthorized access attempts during the exposure window</li>
            <li>Look for successful logins from unknown IPs</li>
            <li>Review for data access or configuration changes</li>
          </ul>

          <h3>Step 5: Plan Proper Access Controls</h3>
          <ul>
            <li>Determine the legitimate access requirements</li>
            <li>Design proper authentication and network controls</li>
            <li>Schedule implementation of permanent solution</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Don't Just Change the Port</div>
            <div className="callout-content">
              <p>
                Moving an admin panel to a non-standard port (e.g., 8443 instead of 443) is
                <strong> not a security control</strong>. Attackers scan all ports. Port obscurity
                buys you hours at most, not protection.
              </p>
            </div>
          </div>
        </section>

        <section id="auth-hardening" className="guide-section">
          <h2>Authentication Hardening</h2>
          <p>
            Every admin panel must meet these authentication requirements:
          </p>

          <h3>Multi-Factor Authentication (MFA)</h3>
          <div className="callout callout-critical">
            <div className="callout-title">MFA Is Mandatory</div>
            <div className="callout-content">
              <p>
                <strong>MFA is not optional for admin interfaces.</strong> Password-only
                authentication is insufficient because:
              </p>
              <ul>
                <li>Users reuse passwords across services</li>
                <li>Credentials are regularly exposed in data breaches</li>
                <li>Phishing attacks target admin credentials</li>
                <li>Brute force attacks succeed against weak passwords</li>
              </ul>
            </div>
          </div>

          <h3>Authentication Requirements Checklist</h3>
          <ul>
            <li><strong>MFA enforced:</strong> TOTP, WebAuthn, or push notification</li>
            <li><strong>Strong password policy:</strong> Minimum 12 characters, complexity requirements</li>
            <li><strong>Account lockout:</strong> Lock after 5-10 failed attempts</li>
            <li><strong>Session timeout:</strong> Automatic logout after inactivity (15-30 minutes)</li>
            <li><strong>Secure session tokens:</strong> HttpOnly, Secure, SameSite cookies</li>
            <li><strong>No default accounts:</strong> Remove or rename admin, root, administrator</li>
            <li><strong>Audit logging:</strong> Log all authentication events</li>
          </ul>

          <h3>What Is NOT Sufficient</h3>
          <ul>
            <li><strong>HTTPS alone:</strong> Encryption in transit doesn't prevent credential attacks</li>
            <li><strong>Strong passwords alone:</strong> Even strong passwords can be phished or leaked</li>
            <li><strong>CAPTCHAs alone:</strong> Help with automation but don't stop determined attackers</li>
            <li><strong>IP logging alone:</strong> Doesn't prevent attacks, only helps investigation</li>
          </ul>

          <div className="example-box">
            <div className="example-title">MFA Implementation Options</div>
            <div className="example-content">
              <p><strong>Best:</strong> WebAuthn/FIDO2 hardware keys (YubiKey, etc.)</p>
              <p><strong>Good:</strong> Authenticator app TOTP (Google Authenticator, Authy)</p>
              <p><strong>Acceptable:</strong> Push notifications (Duo, Microsoft Authenticator)</p>
              <p><strong>Weak:</strong> SMS codes (vulnerable to SIM swapping, but better than nothing)</p>
            </div>
          </div>
        </section>

        <section id="network-restrictions" className="guide-section">
          <h2>Network Restrictions</h2>
          <p>
            Beyond authentication, restrict network access to admin panels:
          </p>

          <h3>Option 1: VPN-Only Access (Recommended)</h3>
          <p>The most secure approach for most organizations:</p>
          <ul>
            <li>Admin panels only accessible from internal network or VPN</li>
            <li>VPN itself requires MFA</li>
            <li>Provides audit trail of who connected when</li>
            <li>Single point of policy enforcement</li>
          </ul>

          <h3>Option 2: IP Allowlist</h3>
          <p>Restrict to known corporate IP addresses:</p>
          <ul>
            <li>Only allow access from office IPs, data center ranges</li>
            <li>Block all other source IPs at firewall/security group</li>
            <li>Requires maintaining IP list as network changes</li>
            <li>May not work well for distributed/remote teams</li>
          </ul>

          <h3>Option 3: Zero Trust / Identity-Aware Proxy</h3>
          <p>Modern approach using identity verification at the network layer:</p>
          <ul>
            <li>Every request authenticated and authorized</li>
            <li>Device posture checking (managed device, patches, etc.)</li>
            <li>No implicit trust based on network location</li>
            <li>Examples: Cloudflare Access, Zscaler Private Access, Google BeyondCorp</li>
          </ul>

          <h3>Cloud Provider Specifics</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Network Controls</th>
                  <th>Additional Protection</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>AWS</td>
                  <td>Security Groups, NACLs</td>
                  <td>AWS WAF, PrivateLink</td>
                </tr>
                <tr>
                  <td>Azure</td>
                  <td>NSG, Azure Firewall</td>
                  <td>Application Gateway, Private Endpoints</td>
                </tr>
                <tr>
                  <td>GCP</td>
                  <td>VPC Firewall Rules</td>
                  <td>Identity-Aware Proxy (IAP), Private Google Access</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-warning">
            <div className="callout-title">Not Acceptable</div>
            <div className="callout-content">
              <p>
                <strong>Open to internet with authentication only is not acceptable for admin panels.</strong>
                Even with MFA, exposing admin interfaces to the entire internet creates unnecessary
                attack surface. Always combine authentication with network restrictions.
              </p>
            </div>
          </div>
        </section>

        <section id="logging-monitoring" className="guide-section">
          <h2>Logging & Monitoring</h2>
          <p>
            Admin panels require comprehensive logging and active monitoring:
          </p>

          <h3>What to Log</h3>
          <ul>
            <li><strong>Authentication events:</strong> All login attempts (success and failure)</li>
            <li><strong>Authorization decisions:</strong> Access grants and denials</li>
            <li><strong>Admin actions:</strong> Configuration changes, user management, data modifications</li>
            <li><strong>Data access:</strong> Queries, exports, bulk operations</li>
            <li><strong>Source information:</strong> IP address, user agent, session ID</li>
          </ul>

          <h3>Alerting Requirements</h3>
          <ul>
            <li><strong>Failed auth threshold:</strong> Alert after N failed attempts from same IP or user</li>
            <li><strong>Unusual access patterns:</strong> Login from new country/IP, unusual hours</li>
            <li><strong>Admin account changes:</strong> New admin created, permissions elevated</li>
            <li><strong>Bulk operations:</strong> Large data exports, mass deletions</li>
            <li><strong>Configuration changes:</strong> Security settings modified</li>
          </ul>

          <h3>Log Retention</h3>
          <ul>
            <li>Retain authentication logs for minimum 90 days</li>
            <li>Admin action logs: 1 year minimum (compliance may require longer)</li>
            <li>Store logs separately from the system being monitored</li>
            <li>Protect logs from tampering (immutable storage, separate permissions)</li>
          </ul>

          <div className="example-box">
            <div className="example-title">Critical Alerts</div>
            <div className="example-content">
              <p>Configure immediate alerts for:</p>
              <ul>
                <li>Successful login after multiple failures</li>
                <li>Login from a new geographic location</li>
                <li>Admin account created outside of change window</li>
                <li>Security settings disabled (MFA, IP restrictions)</li>
                <li>Bulk data export or deletion</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="prevention" className="guide-section">
          <h2>Prevention Controls</h2>
          <p>
            Prevent admin panel exposure from occurring in the first place:
          </p>

          <h3>Asset Tagging & Inventory</h3>
          <ul>
            <li><strong>Tag admin interfaces:</strong> Mark all admin panels in your asset inventory</li>
            <li><strong>Classify sensitivity:</strong> Rate by data access and control capabilities</li>
            <li><strong>Assign owners:</strong> Every admin panel needs a responsible owner</li>
            <li><strong>Track exposure status:</strong> Record whether each is internal/external</li>
            <li><strong>Regular review:</strong> Quarterly audit of all tagged admin interfaces</li>
          </ul>

          <h3>Deployment Guardrails</h3>
          <div className="callout callout-info">
            <div className="callout-title">Shift Left on Security</div>
            <div className="callout-content">
              <p>Build controls into your deployment pipeline to prevent exposure:</p>
            </div>
          </div>

          <ul>
            <li><strong>CI/CD checks:</strong> Deny public deployment of admin paths; fail builds that
                expose /admin, /jenkins, etc. to public</li>
            <li><strong>Infrastructure as Code:</strong> Enforce private subnets for admin interfaces
                in Terraform, CloudFormation, Pulumi</li>
            <li><strong>Cloud policies:</strong> Prevent security groups with 0.0.0.0/0 on admin ports;
                use AWS SCPs, Azure Policy, GCP Org Policies</li>
            <li><strong>Pre-commit hooks:</strong> Scan for exposed admin routes in code before commit</li>
            <li><strong>Container policies:</strong> Block containers with admin UIs from external
                ingress in Kubernetes</li>
          </ul>

          <h3>Organizational Controls</h3>
          <ul>
            <li><strong>Policy:</strong> "No admin interfaces on public internet" as written policy</li>
            <li><strong>Exception process:</strong> Any exception requires security review + MFA +
                IP restriction + documented justification</li>
            <li><strong>Regular audits:</strong> Quarterly review of all externally exposed surfaces</li>
            <li><strong>Training:</strong> Developer awareness of exposure risks and secure defaults</li>
            <li><strong>Incident response:</strong> Documented playbook for exposed admin panels</li>
          </ul>

          <h3>Continuous Monitoring</h3>
          <ul>
            <li><strong>Attack surface scanning:</strong> Regular scans for exposed admin paths</li>
            <li><strong>Certificate monitoring:</strong> Alert on new certificates that might indicate
                new admin panels</li>
            <li><strong>DNS monitoring:</strong> Watch for new subdomains that could host admin UIs</li>
            <li><strong>Port scanning:</strong> Detect new services on admin-related ports</li>
          </ul>
        </section>

        {/* PRD-193 WP4: this guide previously had NO link to /upgrade or
            /how-it-works -- its only exit was a trial button that went to the sign-IN
            form. Mid-article conversion path, matching the block the other guides use. */}
        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>How DriftAlarm Helps</h3>
            <p>Management interfaces rarely get exposed on purpose — they arrive through a firewall change, a new host, or a staging box nobody decommissioned. Continuous external scanning walks your perimeter every cycle, so a panel that becomes reachable shows up as drift rather than sitting unnoticed.</p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Exposed admin panels are often found alongside other attack surface issues. Review these related guides:
          </p>
          <ul>
            <li>
              <Link to="/guides/exposed-rdp">Exposed RDP Guide</Link> — Remote Desktop exposure often accompanies web-based admin panels on Windows servers.
            </li>
            <li>
              <Link to="/guides/exposed-ssh">Exposed SSH Guide</Link> — SSH access is commonly found on the same hosts as exposed admin interfaces.
            </li>
            <li>
              <Link to="/guides/cloud-storage-exposure">Cloud Storage Exposure Guide</Link> — Misconfigured storage often happens alongside exposed admin consoles in cloud deployments.
            </li>
            <li>
              <Link to="/guides/eol-software">EOL Software Guide</Link> — Outdated admin panels (old WordPress, legacy Jenkins) compound the risk of exposure.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — Learn how to continuously discover exposed admin panels across your perimeter.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary: Admin Panel Response Checklist</h2>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Timeframe</th>
                  <th>Action</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>0-30 min</td>
                  <td>Block external access at network level</td>
                  <td>Network/Security</td>
                </tr>
                <tr>
                  <td>30-60 min</td>
                  <td>Verify block, notify asset owner</td>
                  <td>Security</td>
                </tr>
                <tr>
                  <td>1-4 hours</td>
                  <td>Review logs for unauthorized access</td>
                  <td>Security</td>
                </tr>
                <tr>
                  <td>1-7 days</td>
                  <td>Implement proper access (VPN + MFA)</td>
                  <td>IT/Security</td>
                </tr>
                <tr>
                  <td>Ongoing</td>
                  <td>Monitor, enforce prevention controls</td>
                  <td>Security</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/exposed-admin-panels" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default ExposedAdminPanelsGuide;
